import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClock, FiChevronDown, FiChevronRight, FiCopy, FiCheck } = FiIcons;

function ApiLogPanel({ logs }) {
  const [filter, setFilter] = useState('all');
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const [copySuccess, setCopySuccess] = useState('');
  const logEndRef = useRef(null);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const filters = [
    { id: 'all', label: 'All', count: logs.length },
    { id: 'success', label: 'Success', count: logs.filter(log => log.type === 'success' || (log.status && log.status < 400)).length },
    { id: 'error', label: 'Error', count: logs.filter(log => log.type === 'error' || (log.status && log.status >= 400)).length },
    { id: 'ncb', label: 'NCB', count: logs.filter(log => log.api === 'ncb').length },
    { id: 'dropbox', label: 'Dropbox', count: logs.filter(log => log.api === 'dropbox').length }
  ];

  const filteredLogs = logs.filter(log => {
    switch (filter) {
      case 'success':
        return log.type === 'success' || (log.status && log.status < 400);
      case 'error':
        return log.type === 'error' || (log.status && log.status >= 400);
      case 'ncb':
        return log.api === 'ncb';
      case 'dropbox':
        return log.api === 'dropbox';
      default:
        return true;
    }
  });

  const toggleExpanded = (logId) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  const copyToClipboard = async (text, type, logId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`${type}-${logId}`);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case 'GET': return 'bg-success-100 text-success-800';
      case 'POST': return 'bg-blue-100 text-blue-800';
      case 'PUT': return 'bg-warning-100 text-warning-800';
      case 'DELETE': return 'bg-error-100 text-error-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    if (status < 300) return 'bg-success-100 text-success-800';
    if (status < 400) return 'bg-blue-100 text-blue-800';
    if (status < 500) return 'bg-warning-100 text-warning-800';
    return 'bg-error-100 text-error-800';
  };

  const formatJson = (data) => {
    if (typeof data === 'string') {
      try {
        return JSON.stringify(JSON.parse(data), null, 2);
      } catch {
        return data;
      }
    }
    return JSON.stringify(data, null, 2);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">API Logs</h2>
          <span className="text-sm text-gray-500">
            {filteredLogs.length} of {logs.length} logs
          </span>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          {filters.map(filterOption => (
            <button
              key={filterOption.id}
              onClick={() => setFilter(filterOption.id)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                filter === filterOption.id
                  ? 'bg-primary-100 text-primary-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filterOption.label} ({filterOption.count})
            </button>
          ))}
        </div>
      </div>

      {/* Logs List */}
      <div className="flex-1 overflow-y-auto">
        {filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-500">
            <SafeIcon icon={FiClock} className="h-8 w-8 mb-2" />
            <p className="text-sm">No logs yet. Execute an API request to see logs here.</p>
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {filteredLogs.map(log => (
              <div key={log.id} className="border rounded-lg overflow-hidden">
                {/* Log Header */}
                <button
                  onClick={() => toggleExpanded(log.id)}
                  className="w-full p-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <SafeIcon 
                        icon={expandedLogs.has(log.id) ? FiChevronDown : FiChevronRight} 
                        className="h-4 w-4 text-gray-400 flex-shrink-0" 
                      />
                      
                      {log.method && (
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getMethodColor(log.method)}`}>
                          {log.method}
                        </span>
                      )}
                      
                      {log.status && (
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(log.status)}`}>
                          {log.status}
                        </span>
                      )}
                      
                      <span className="text-sm text-gray-900 truncate">
                        {log.url || log.message}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-xs text-gray-500 flex-shrink-0">
                      {log.duration && <span>{log.duration}ms</span>}
                      <span>{format(new Date(log.timestamp), 'HH:mm:ss')}</span>
                    </div>
                  </div>
                </button>

                {/* Expanded Content */}
                {expandedLogs.has(log.id) && (
                  <div className="border-t bg-gray-50 p-4 space-y-4">
                    {/* Request */}
                    {log.request && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-700">Request</h4>
                          <button
                            onClick={() => copyToClipboard(formatJson(log.request), 'request', log.id)}
                            className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700"
                          >
                            <SafeIcon 
                              icon={copySuccess === `request-${log.id}` ? FiCheck : FiCopy} 
                              className="h-3 w-3" 
                            />
                            <span>{copySuccess === `request-${log.id}` ? 'Copied!' : 'Copy'}</span>
                          </button>
                        </div>
                        <pre className="json-syntax text-xs">
                          {formatJson(log.request)}
                        </pre>
                      </div>
                    )}

                    {/* Response */}
                    {log.response && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-700">Response</h4>
                          <button
                            onClick={() => copyToClipboard(formatJson(log.response), 'response', log.id)}
                            className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700"
                          >
                            <SafeIcon 
                              icon={copySuccess === `response-${log.id}` ? FiCheck : FiCopy} 
                              className="h-3 w-3" 
                            />
                            <span>{copySuccess === `response-${log.id}` ? 'Copied!' : 'Copy'}</span>
                          </button>
                        </div>
                        <pre className="json-syntax text-xs">
                          {formatJson(log.response)}
                        </pre>
                      </div>
                    )}

                    {/* Error */}
                    {log.error && (
                      <div>
                        <h4 className="text-sm font-medium text-error-700 mb-2">Error</h4>
                        <div className="bg-error-50 border border-error-200 rounded p-3">
                          <p className="text-sm text-error-800">{log.error}</p>
                        </div>
                      </div>
                    )}

                    {/* Headers */}
                    {log.headers && Object.keys(log.headers).length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-700">Headers</h4>
                          <button
                            onClick={() => copyToClipboard(formatJson(log.headers), 'headers', log.id)}
                            className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700"
                          >
                            <SafeIcon 
                              icon={copySuccess === `headers-${log.id}` ? FiCheck : FiCopy} 
                              className="h-3 w-3" 
                            />
                            <span>{copySuccess === `headers-${log.id}` ? 'Copied!' : 'Copy'}</span>
                          </button>
                        </div>
                        <pre className="json-syntax text-xs">
                          {formatJson(log.headers)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div ref={logEndRef} />
          </div>
        )}
      </div>
    </div>
  );
}

export default ApiLogPanel;